import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { getSupabaseBrowserClient } from '@/lib/supabase';

export function useRadarSignOut(session: Session | null, onSignedOut: () => void) {
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const [signOutFailed, setSignOutFailed] = useState(false);
  const pending = useRef(false);
  const mounted = useRef(true);
  const currentSession = useRef(session);
  currentSession.current = session;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { setSignOutFailed(false); }, [session?.user.id]);

  async function signOut() {
    if (!session || pending.current) return;
    const userId = session.user.id;
    pending.current = true;
    setSigningOut(true);
    setSignOutFailed(false);
    try {
      // Do not revoke independent sessions on other browsers/devices.
      const { error } = await getSupabaseBrowserClient().auth.signOut({ scope: 'local' });
      if (error) throw error;
      if (!mounted.current || (currentSession.current && currentSession.current.user.id !== userId)) return;
      onSignedOut();
      // Drop the old workspace URL so a different account starts at its own entry.
      navigate('/buyer-radar/app', { replace: true });
    } catch {
      if (mounted.current && currentSession.current?.user.id === userId) setSignOutFailed(true);
    } finally {
      pending.current = false;
      if (mounted.current) setSigningOut(false);
    }
  }

  return { signOut, signingOut, signOutFailed };
}
